import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';
import { FormattedMessage } from 'dibs-react-intl';

import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { PdpSharedDescriptionAbout } from '../../PdpSharedDescriptions/PdpSharedDescriptionAbout.tsx';
import { PdpRespDetails } from '../../PdpRespDetails/PdpRespDetails.tsx';

import { type PdpRespSharedAboutItemPanel_item$key } from './__generated__/PdpRespSharedAboutItemPanel_item.graphql.ts';
import { type PdpRespSharedAboutItemPanel_user$key } from './__generated__/PdpRespSharedAboutItemPanel_user.graphql.ts';

type Props = {
    showHeader?: boolean;
    item: PdpRespSharedAboutItemPanel_item$key;
    user: PdpRespSharedAboutItemPanel_user$key | null | undefined;
    currency: string;
    selectedSkuId: string | null;
    showExpandingArea?: boolean;
    afterDisplay: boolean;
    dataTn?: string;
    children?: ReactNode;
    showThirdCol: boolean;
};

const itemFragment = graphql`
    fragment PdpRespSharedAboutItemPanel_item on Item {
        ...PdpSharedDescriptionAbout_item
        ...PdpRespDetails_item
    }
`;

const userFragment = graphql`
    fragment PdpRespSharedAboutItemPanel_user on User {
        ...PdpRespDetails_user
    }
`;

export const PdpRespSharedAboutItemPanel: FC<Props> = ({
    showHeader,
    item: itemRef,
    user: userRef,
    currency,
    selectedSkuId,
    showExpandingArea,
    dataTn,
    afterDisplay,
    children,
    showThirdCol,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const { isTablet } = useServerVarsContext();

    // For details collapsed height = [# of lines to show] * ([line-height] + [margin height]) - [margin of container]
    const singleColumnDetailsHeight = 9 * (24 + 9) - 9;

    return (
        <GridRow dataTn={dataTn}>
            <GridCol
                desktop={showThirdCol ? 4 : 5}
                tabletPortrait={showThirdCol ? 6 : 12}
                tabletLandscape={showThirdCol ? 6 : undefined}
            >
                <div className={dibsCss.mbSmall}>
                    <PdpSharedDescriptionAbout
                        item={item}
                        headerClass={
                            showHeader
                                ? classnames(
                                      dibsCss.sassyFontBodyMediumHeavy,
                                      dibsCss.m0,
                                      dibsCss.mrXsmall
                                  )
                                : dibsCss.hidden
                        }
                        title={
                            <FormattedMessage
                                id="PdpRespSharedAboutItemPanel.aboutHeader"
                                defaultMessage="About the Item"
                            />
                        }
                        // line height * number of lines
                        collapsedHeight={24 * 10}
                        showExpandingArea={showExpandingArea}
                    />
                </div>
            </GridCol>
            {showThirdCol && isTablet && children}
            <GridCol
                desktop={showThirdCol ? 4 : 6}
                desktopOptions={
                    !showThirdCol
                        ? {
                              offset: 1,
                          }
                        : undefined
                }
                tabletPortrait={12}
                tabletLandscape={12}
            >
                {/* Details */}
                <PdpRespDetails
                    variant={
                        showThirdCol && !isTablet ? 'pdpRespTabsVariant' : 'pdpRedesignVariant'
                    }
                    showHeader={false}
                    afterDisplay={afterDisplay}
                    currency={currency}
                    item={item}
                    selectedSkuId={selectedSkuId}
                    user={user}
                    collapsedHeight={singleColumnDetailsHeight}
                    showTopBorder={isTablet}
                />
            </GridCol>
            {showThirdCol && !isTablet && children}
        </GridRow>
    );
};
